// Authed API client (architecture.md §19). One fetch wrapper: injects Authorization:
// Bearer <jwt>, adds Idempotency-Key on booking/payment POSTs, maps 401→refresh/logout
// and 403→upgrade/permission UI. Base URL from env.
import { supabase } from './supabase.js';

const BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';

function uuid() {
  return (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random()}`;
}

async function getToken() {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token || null;
}

async function request(path, { method = 'GET', body, idempotent = false } = {}) {
  const token = await getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (idempotent) headers['Idempotency-Key'] = uuid();

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.error?.message || `Request failed (${res.status})`);
    err.status = res.status;
    err.code = data?.error?.code;
    err.details = data?.error?.details;
    if (res.status === 401) await supabase.auth.signOut(); // token invalid → logout
    throw err;
  }
  return data;
}

export const api = {
  raw: request,
  // Auth (signup + email verification go through the API; login/refresh via supabase-js directly).
  signup: (body) => request('/auth/signup', { method: 'POST', body }),
  verifyOtp: (email, code) => request('/auth/verify-otp', { method: 'POST', body: { email, code } }),
  resendOtp: (email) => request('/auth/resend-otp', { method: 'POST', body: { email } }),
  me: () => request('/me'),
  updateMe: (body) => request('/me', { method: 'PATCH', body }),
  uploadAvatar: async (file) => {
    // Multipart upload — let the browser set the Content-Type boundary itself.
    const token = await getToken();
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${BASE}/me/avatar`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data?.error?.message || `Upload failed (${res.status})`);
      err.status = res.status;
      throw err;
    }
    return data;
  },

  // Event types
  listEventTypes: () => request('/event-types'),
  createEventType: (body) => request('/event-types', { method: 'POST', body }),
  updateEventType: (id, body) => request(`/event-types/${id}`, { method: 'PATCH', body }),
  deleteEventType: (id) => request(`/event-types/${id}`, { method: 'DELETE' }),
  getEventAvailability: (id) => request(`/event-types/${id}/availability`),
  updateEventAvailability: (id, body) => request(`/event-types/${id}/availability`, { method: 'PUT', body }),

  // Availability
  getAvailability: () => request('/availability'),
  updateAvailability: (body) => request('/availability', { method: 'PUT', body }),

  // Bookings
  listBookings: (qs = '') => request(`/bookings${qs}`),
  bookingKpis: () => request('/bookings/kpis'),
  getBooking: (id) => request(`/bookings/${id}`),
  cancelBooking: (id) => request(`/bookings/${id}/cancel`, { method: 'POST' }),
  completeBooking: (id) => request(`/bookings/${id}/complete`, { method: 'POST' }),

  // Form questions (M4)
  listFormQuestions: () => request('/form-questions'),
  createFormQuestion: (body) => request('/form-questions', { method: 'POST', body }),
  updateFormQuestion: (id, body) => request(`/form-questions/${id}`, { method: 'PATCH', body }),
  deleteFormQuestion: (id) => request(`/form-questions/${id}`, { method: 'DELETE' }),

  // Host promo codes (M8)
  listPromos: () => request('/promo-codes'),
  createPromo: (body) => request('/promo-codes', { method: 'POST', body }),
  updatePromo: (id, isDisabled) => request(`/promo-codes/${id}`, { method: 'PATCH', body: { is_disabled: isDisabled } }),
  deletePromo: (id) => request(`/promo-codes/${id}`, { method: 'DELETE' }),

  // API v1 tokens (Pro, M12)
  listApiTokens: () => request('/api-tokens'),
  createApiToken: (name) => request('/api-tokens', { method: 'POST', body: { name } }),
  revokeApiToken: (id) => request(`/api-tokens/${id}`, { method: 'DELETE' }),

  // Custom domains (M12)
  getDomain: () => request('/domains'),
  setDomain: (domain) => request('/domains', { method: 'POST', body: { domain } }),
  verifyDomain: () => request('/domains/verify', { method: 'POST' }),
  removeDomain: () => request('/domains', { method: 'DELETE' }),

  // Plans (public)
  listPlans: () => request('/plans'),

  // Manual payment info (public read; admin-configured)
  getPaymentInfo: () => request('/public/payment-info'),

  // Lifetime deal (public read; admin-configured)
  getLifetimeDeal: () => request('/public/lifetime-deal'),

  // Legal / info page content (public read; admin-editable)
  getLegalPages: () => request('/public/legal-pages'),
  adminUploadPaymentQr: async (file) => {
    const token = await getToken();
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${BASE}/admin/payment-qr`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data?.error?.message || `Upload failed (${res.status})`);
      err.status = res.status;
      throw err;
    }
    return data;
  },
  adminUploadLifetimeDealQr: async (file) => {
    const token = await getToken();
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${BASE}/admin/lifetime-deal-qr`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data?.error?.message || `Upload failed (${res.status})`);
      err.status = res.status;
      throw err;
    }
    return data;
  },

  // Agency / Team booking (Phase 0)
  agencyContext: () => request('/agency/context'),
  createAgency: (body) => request('/agency', { method: 'POST', body }),
  listEmployees: () => request('/agency/employees'),
  createEmployee: (body) => request('/agency/employees', { method: 'POST', body }),
  updateEmployee: (id, body) => request(`/agency/employees/${id}`, { method: 'PATCH', body }),
  resetEmployeePassword: (id, password) =>
    request(`/agency/employees/${id}/reset-password`, { method: 'POST', body: { password } }),
  suspendEmployee: (id) => request(`/agency/employees/${id}/suspend`, { method: 'POST' }),
  reactivateEmployee: (id) => request(`/agency/employees/${id}/reactivate`, { method: 'POST' }),
  removeEmployee: (id) => request(`/agency/employees/${id}`, { method: 'DELETE' }),
  employeeSetPassword: (password) =>
    request('/agency/me/password', { method: 'PATCH', body: { password } }),

  // Admin
  adminListUsers: (qs = '') => request(`/admin/users${qs}`),
  adminDisableUser: (id) => request(`/admin/users/${id}/disable`, { method: 'POST' }),
  adminEnableUser: (id) => request(`/admin/users/${id}/enable`, { method: 'POST' }),
  adminChangePlan: (id, planId, validDays = 0) => request(`/admin/users/${id}/change-plan`, { method: 'POST', body: { plan_id: planId, valid_days: validDays } }),
  adminResetPassword: (id, password) => request(`/admin/users/${id}/reset-password`, { method: 'POST', body: { password } }),
  adminListPlans: () => request('/admin/plans'),
  adminCreatePlan: (body) => request('/admin/plans', { method: 'POST', body }),
  adminUpdatePlan: (id, body) => request(`/admin/plans/${id}`, { method: 'PATCH', body }),
  adminDeletePlan: (id) => request(`/admin/plans/${id}`, { method: 'DELETE' }),
  adminListPlatformPromos: () => request('/admin/promo-codes'),
  adminCreatePlatformPromo: (body) => request('/admin/promo-codes', { method: 'POST', body }),
  adminUpdatePlatformPromo: (id, isDisabled) => request(`/admin/promo-codes/${id}`, { method: 'PATCH', body: { is_disabled: isDisabled } }),
  adminDeletePlatformPromo: (id) => request(`/admin/promo-codes/${id}`, { method: 'DELETE' }),
  adminGetSettings: () => request('/admin/settings'),
  adminUpdateSettings: (body) => request('/admin/settings', { method: 'PUT', body }),
  adminListLegalPages: () => request('/admin/legal-pages'),
  adminUpdateLegalPage: (slug, content) => request(`/admin/legal-pages/${slug}`, { method: 'PUT', body: { content } }),
  adminListAuditLogs: (qs = '') => request(`/admin/audit-logs${qs}`),
  adminListAllBookings: () => request('/admin/bookings'),
  adminSendBookingEmail: (id, template) => request(`/admin/bookings/${id}/send-email`, { method: 'POST', body: { template } }),
};
